// 🔗 Cliente de Supabase para la base de datos "selger"
// Configuración y conexión con Supabase

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { safeFetch } from '../utils/supabaseHelpers';

// Configuración de Supabase desde variables de entorno
// Limpiar espacios y caracteres extra
const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').trim();
const supabaseAnonKey = (process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();

// Logs de debug (siempre mostrar para diagnóstico)
console.log('🔍 [Supabase Config]');
console.log('  URL:', supabaseUrl || 'NO CONFIGURADA');
console.log('  Key (first 30):', supabaseAnonKey ? `${supabaseAnonKey.substring(0, 30)}...` : 'NO CONFIGURADA');
console.log('  Key (last 20):', supabaseAnonKey ? `...${supabaseAnonKey.substring(supabaseAnonKey.length - 20)}` : 'NO CONFIGURADA');
console.log('  NODE_ENV:', process.env.NODE_ENV);
console.log('  URL length:', supabaseUrl.length);
console.log('  Key length:', supabaseAnonKey.length);
console.log('  Key starts with:', supabaseAnonKey ? supabaseAnonKey.substring(0, 10) : 'N/A');

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Supabase: Variables de entorno no configuradas.');
  console.error('   REACT_APP_SUPABASE_URL:', supabaseUrl || 'FALTA');
  console.error('   REACT_APP_SUPABASE_ANON_KEY:', supabaseAnonKey ? 'CONFIGURADA' : 'FALTA');
  throw new Error('Supabase configuration missing: REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY are required');
}

// ✅ CORREGIDO: Cliente de Supabase con configuración correcta para refresh automático de tokens
// ✅ NUEVO: Usar sessionStorage para sesión de pestaña (se limpia al cerrar la pestaña)
export const supabase: SupabaseClient = createClient(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
      persistSession: true, // Mantener durante la sesión de la pestaña
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    // Configuración de timeout global
    global: {
      headers: {
        'x-client-info': 'selger-app',
        'apikey': supabaseAnonKey // Asegurar que la API key esté en los headers
        // ❌ ELIMINADO: 'Content-Type': 'application/json'
        // Este header causaba que todas las peticiones (incluyendo Storage) usaran application/json
        // Dejar que cada petición establezca su propio Content-Type automáticamente
      }
    },
    // Configuración de db para mejorar performance
    db: {
      schema: 'public'
    }
  }
);

// Función para verificar la conexión con Supabase (con timeout)
export async function testSupabaseConnection(): Promise<boolean> {
  try {
    const result = await safeFetch(
      async () => {
        const { error } = await supabase.from('gastos').select('count').limit(1);
        if (error && error.code !== 'PGRST116') {
          throw new Error(`Error de conexión: ${error.message}`);
        }
        return true;
      },
      {
        timeout: 10000, // 10 segundos para test de conexión
        retries: 2,
        errorMessage: 'Timeout al verificar conexión con Supabase'
      }
    );
    
    console.log('✅ Conexión con Supabase exitosa');
    return result;
  } catch (error) {
    console.error('❌ Error al conectar con Supabase:', error);
    return false;
  }
}

// Tipos de respuesta de Supabase
export interface SupabaseResponse<T> {
  data: T | null;
  error: any | null;
}

// Wrapper para consultas Supabase con timeout y retry
export async function safeSupabaseQuery<T>(
  queryFn: () => Promise<{ data: T | null; error: any | null }>,
  options: {
    timeout?: number;
    retries?: number;
  } = {}
): Promise<{ data: T | null; error: any | null }> {
  try {
    const result = await safeFetch(queryFn, {
      timeout: options.timeout || 30000,
      retries: options.retries || 3,
      errorMessage: 'La consulta a Supabase tardó demasiado'
    });
    return result;
  } catch (error: any) {
    return {
      data: null,
      error: {
        message: error?.message || 'Error desconocido en Supabase',
        code: 'TIMEOUT_ERROR'
      }
    };
  }
}

export default supabase;

